import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Plus, Save, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import dayjs from "dayjs";
import { api, DOC_TYPE_LABELS, errMsg, inr } from "@/lib/api";
import { EntityCombobox, Field, PageHeader, StateSelect } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

const STEPS = ["Customer", "Booking", "Items", "GST Config", "Discount", "Payment", "Review"];

const r2 = (x) => Math.round((Number(x) + Number.EPSILON) * 100) / 100;

export default function InvoiceCreate() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [settings, setSettings] = useState(null);
  const [taxRates, setTaxRates] = useState([0, 5, 12, 18, 28]);
  const [invoices, setInvoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [nextNo, setNextNo] = useState("");

  const [form, setForm] = useState({
    doc_type: params.get("doc_type") || "tax_invoice",
    date: dayjs().format("YYYY-MM-DD"), due_date: dayjs().add(15, "day").format("YYYY-MM-DD"),
    customer_id: params.get("customer") || "", booking_id: params.get("booking") || "",
    agent_id: "", place_of_supply: "", place_of_supply_code: "",
    reverse_charge: false, tax_type: "regular",
    items: [], overall_discount: 0, notes: "", reason: "", original_invoice_id: "",
    payment: { amount: 0, mode: "UPI", reference: "" },
  });

  useEffect(() => {
    Promise.all([
      api.get("/customers?limit=200"), api.get("/services?active_only=true&limit=200"),
      api.get("/bookings?limit=200"), api.get("/settings/business"),
      api.get("/settings/tax-rates"),
    ]).then(([c, s, b, biz, tr]) => {
      setCustomers(c.data.items);
      setServices(s.data.items);
      setBookings(b.data.items);
      setSettings(biz.data);
      setTaxRates(tr.data.filter((t) => t.active).map((t) => t.rate));
    }).catch((e) => toast.error(errMsg(e)));
  }, []);

  useEffect(() => {
    if (["credit_note", "debit_note"].includes(form.doc_type)) {
      api.get("/invoices?doc_type=tax_invoice&limit=200").then((r) =>
        setInvoices(r.data.items.filter((i) => ["issued", "partial", "paid"].includes(i.status)))).catch(() => {});
    }
  }, [form.doc_type]);

  useEffect(() => {
    api.get(`/invoices/meta/next-number?doc_type=${form.doc_type}`)
      .then((r) => setNextNo(r.data.next_number)).catch(() => {});
  }, [form.doc_type]);

  // Prefill from booking/quotation
  useEffect(() => {
    const bk = form.booking_id && bookings.find((b) => b.id === form.booking_id);
    if (bk) {
      setForm((f) => ({
        ...f, customer_id: f.customer_id || bk.customer_id, agent_id: bk.agent_id || "",
        items: f.items.length ? f.items : [{
          description: `${bk.package_name} — ${bk.destination} (${bk.start_date} to ${bk.end_date})`,
          sac: "998551", qty: 1, unit: "Package", rate: bk.package_cost, discount: bk.discount,
          gst_rate: bk.gst_rate ?? 5,
        }],
      }));
    }
  }, [form.booking_id, bookings]);

  useEffect(() => {
    const qid = params.get("quotation");
    if (qid) {
      api.get(`/quotations/${qid}`).then((r) => {
        const q = r.data;
        setForm((f) => ({
          ...f, customer_id: q.customer_id,
          items: q.items.map((it) => ({ description: it.description, sac: it.sac, qty: it.qty,
            unit: it.unit, rate: it.rate, discount: it.discount, gst_rate: it.gst_rate })),
          notes: `Converted from quotation ${q.quotation_no}`,
        }));
      }).catch(() => {});
    }
  }, [params]);

  const customer = customers.find((c) => c.id === form.customer_id);

  useEffect(() => {
    if (customer && !form.place_of_supply) {
      setForm((f) => ({ ...f, place_of_supply: customer.state || "",
        place_of_supply_code: customer.state_code || "" }));
    }
  }, [form.customer_id, customers]);

  const supplierCode = settings?.state_code || "29";
  const totals = useMemo(() => {
    let taxable = 0, cgst = 0, sgst = 0, igst = 0;
    const base = form.items.reduce((s, it) => s + r2(it.qty * it.rate - (it.discount || 0)), 0) || 1;
    form.items.forEach((it) => {
      let t = r2((it.qty || 1) * (it.rate || 0) - (it.discount || 0));
      if (form.overall_discount > 0) {
        t = r2(t - form.overall_discount * (t / base));
      }
      const rate = form.tax_type === "regular" ? Number(it.gst_rate || 0) : 0;
      taxable += t;
      if (rate > 0 && form.place_of_supply_code) {
        if (form.place_of_supply_code === supplierCode) {
          cgst += r2(t * rate / 200); sgst += r2(t * rate / 200);
        } else {
          igst += r2(t * rate / 100);
        }
      }
    });
    const gst = r2(cgst + sgst + igst);
    const raw = r2(taxable) + gst;
    const grand = Math.round(raw);
    return { taxable: r2(taxable), cgst: r2(cgst), sgst: r2(sgst), igst: r2(igst), gst, roundOff: r2(grand - raw), grand };
  }, [form.items, form.tax_type, form.place_of_supply_code, form.overall_discount, supplierCode]);

  const isIgst = form.place_of_supply_code && form.place_of_supply_code !== supplierCode;

  const addItem = (svc) => {
    setForm((f) => ({ ...f, items: [...f.items, svc
      ? { service_id: svc.id, description: svc.name, sac: svc.sac, qty: 1, unit: svc.unit, rate: svc.price, discount: 0, gst_rate: svc.gst_rate }
      : { description: "", sac: "", qty: 1, unit: "Nos", rate: 0, discount: 0, gst_rate: settings?.default_gst_rate ?? 5 }] }));
  };
  const setItem = (i, patch) => setForm((f) => {
    const items = [...f.items];
    items[i] = { ...items[i], ...patch };
    return { ...f, items };
  });

  const submit = async (saveAs) => {
    if (!form.customer_id) { toast.error("Select a customer (Step 1)"); setStep(0); return; }
    if (!form.items.length || form.items.some((i) => !i.description)) {
      toast.error("Add at least one item with a description (Step 3)"); setStep(2); return;
    }
    setSaving(true);
    try {
      const payload = { ...form,
        payment: form.payment.amount > 0 ? form.payment : null, save_as: saveAs };
      const r = await api.post("/invoices", payload);
      (r.data.warnings || []).forEach((w) => toast.warning(w, { duration: 6000 }));
      toast.success(saveAs === "draft" ? "Draft saved" : `Invoice ${r.data.invoice.invoice_no} generated`);
      navigate(`/invoices/${r.data.invoice.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };

  const canNext = step === 0 ? !!form.customer_id : step === 2 ? form.items.length > 0 : true;

  return (
    <div className="max-w-6xl mx-auto" data-testid="invoice-create-page">
      <PageHeader title={`New ${DOC_TYPE_LABELS[form.doc_type] || "Invoice"}`}
        subtitle={nextNo ? `Next number: ${nextNo}` : "GST-compliant document"}
        testid="invoice-create-header"
        actions={<Button variant="outline" onClick={() => navigate(-1)} data-testid="invoice-create-back"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>} />

      <div className="flex gap-2 flex-wrap mb-6">
        {STEPS.map((s, i) => (
          <button key={s} onClick={() => setStep(i)} data-testid={`invoice-wizard-step-${i + 1}`}
            className={cn("flex items-center gap-2 text-xs font-medium py-2 px-3 rounded-lg border transition-colors",
              i === step ? "bg-[#0D4F4A] text-white border-[#0D4F4A] font-semibold" :
              i < step ? "bg-[#C9A227]/15 text-[#0D4F4A] border-[#C9A227]/50" :
              "bg-[#FAF8F5] text-[#5C6B69] border-[#E5E0D8]")}>
            <span className="h-4 w-4 rounded-full bg-white/20 text-[10px] flex items-center justify-center font-bold">{i + 1}</span>
            {s}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-6 min-h-[380px]">
          {step === 0 && (
            <div className="space-y-5">
              <h3 className="font-display text-lg font-semibold">Select Customer</h3>
              <Field label="Document Type" required>
                <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                  <SelectTrigger data-testid="invoice-doctype-select" className="bg-white border-[#E5E0D8]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              {["credit_note", "debit_note"].includes(form.doc_type) && (
                <>
                  <Field label="Original Invoice" required>
                    <EntityCombobox testid="invoice-original-select" value={form.original_invoice_id}
                      onChange={(v) => {
                        const orig = invoices.find((i) => i.id === v);
                        setForm((f) => ({ ...f, original_invoice_id: v,
                          customer_id: orig ? orig.customer_id : f.customer_id,
                          items: orig ? orig.items.map((it) => ({ description: it.description, sac: it.sac, qty: it.qty, unit: it.unit, rate: it.rate, discount: 0, gst_rate: it.gst_rate })) : f.items }));
                      }}
                      items={invoices.map((i) => ({ value: i.id, label: `${i.invoice_no} · ${i.customer?.name}`, sub: inr(i.grand_total) }))}
                      placeholder="Select original invoice…" />
                  </Field>
                  <Field label="Reason" required>
                    <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      data-testid="invoice-reason-input" className="bg-white border-[#E5E0D8]" placeholder="e.g. Service cancelled / rate revision" />
                  </Field>
                </>
              )}
              <Field label="Customer" required>
                <EntityCombobox testid="invoice-customer-select" value={form.customer_id}
                  onChange={(v) => setForm({ ...form, customer_id: v, place_of_supply: "", place_of_supply_code: "" })}
                  items={customers.map((c) => ({ value: c.id, label: c.name,
                    sub: `${c.gst_treatment?.toUpperCase()} · ${c.state || ""}${c.gstin ? " · " + c.gstin : ""}${c.is_demo ? " · DEMO" : ""}` }))}
                  placeholder="Search customer by name, GSTIN…" />
              </Field>
              {customer && (
                <div className="bg-[#FAF8F5] border border-[#E5E0D8] rounded-lg p-4 text-sm space-y-1" data-testid="invoice-customer-summary">
                  <div className="font-semibold">{customer.name} {customer.company && `· ${customer.company}`}</div>
                  <div className="text-[#5C6B69]">{[customer.address, customer.city, customer.state, customer.pin].filter(Boolean).join(", ")}</div>
                  <div className="text-[#5C6B69]">
                    {customer.gstin ? `GSTIN: ${customer.gstin}` : "No GSTIN (B2C)"} · {customer.phone}
                  </div>
                  <span className={cn("inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-bold",
                    customer.gstin ? "bg-[#0D4F4A]/10 text-[#0D4F4A]" : "bg-[#C9A227]/15 text-[#8a6d14]")}>
                    {customer.gstin ? "B2B" : "B2C"}
                  </span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Invoice Date" required>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    data-testid="invoice-date-input" className="bg-white border-[#E5E0D8]" />
                </Field>
                <Field label="Due Date">
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    data-testid="invoice-due-date-input" className="bg-white border-[#E5E0D8]" />
                </Field>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <h3 className="font-display text-lg font-semibold">Link Booking <span className="text-xs font-normal text-[#5C6B69]">(optional)</span></h3>
              <Field label="Booking">
                <EntityCombobox testid="invoice-booking-select" value={form.booking_id}
                  onChange={(v) => setForm({ ...form, booking_id: v })}
                  items={[{ value: "", label: "— No booking —" }, ...bookings.map((b) => ({
                    value: b.id, label: `${b.booking_id} · ${b.customer_name}`,
                    sub: `${b.destination} · ${b.start_date} · ${b.status}` }))]}
                  placeholder="Select booking…" />
              </Field>
              {form.booking_id && (() => {
                const bk = bookings.find((b) => b.id === form.booking_id);
                return bk ? (
                  <div className="bg-[#FAF8F5] border border-[#E5E0D8] rounded-lg p-4 text-sm grid grid-cols-2 gap-2" data-testid="invoice-booking-summary">
                    <div><span className="text-[#5C6B69]">Package:</span> {bk.package_name}</div>
                    <div><span className="text-[#5C6B69]">Destination:</span> {bk.destination}</div>
                    <div><span className="text-[#5C6B69]">Travel:</span> {bk.start_date} → {bk.end_date}</div>
                    <div><span className="text-[#5C6B69]">Travellers:</span> {bk.travellers}</div>
                    <div><span className="text-[#5C6B69]">Vehicle:</span> {bk.vehicle || "—"}</div>
                    <div><span className="text-[#5C6B69]">Booking total:</span> {inr(bk.total)}</div>
                  </div>
                ) : null;
              })()}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-display text-lg font-semibold">Services / Items</h3>
                <div className="flex gap-2">
                  <EntityCombobox testid="invoice-add-service" value="" onChange={(v) => v && addItem(services.find((s) => s.id === v))}
                    items={services.map((s) => ({ value: s.id, label: s.name, sub: `${s.sac} · ${s.gst_rate}% · ${inr(s.price)}` }))}
                    placeholder="+ From catalog…" />
                  <Button variant="outline" size="sm" onClick={() => addItem(null)} data-testid="invoice-add-custom-item">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Custom Item
                  </Button>
                </div>
              </div>
              {form.items.length === 0 && <p className="text-sm text-[#5C6B69] py-8 text-center">No items yet — add from catalog or a custom line.</p>}
              <div className="space-y-3">
                {form.items.map((it, i) => (
                  <div key={i} className="border border-[#E5E0D8] rounded-lg p-3 space-y-2 bg-[#FAF8F5]/50" data-testid={`invoice-item-${i}`}>
                    <div className="flex gap-2">
                      <Input value={it.description} placeholder="Description" className="bg-white border-[#E5E0D8] flex-1"
                        onChange={(e) => setItem(i, { description: e.target.value })} data-testid={`invoice-item-${i}-desc`} />
                      <Input value={it.sac} placeholder="SAC" className="bg-white border-[#E5E0D8] w-24"
                        onChange={(e) => setItem(i, { sac: e.target.value })} data-testid={`invoice-item-${i}-sac`} />
                      <Button size="icon" variant="ghost" className="text-red-500 shrink-0"
                        onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, x) => x !== i) }))}
                        data-testid={`invoice-item-${i}-remove`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      <div><label className="text-[10px] font-semibold text-[#5C6B69] uppercase">Qty</label>
                        <Input type="number" min="0" value={it.qty} className="bg-white border-[#E5E0D8] h-9"
                          onChange={(e) => setItem(i, { qty: Number(e.target.value) })} data-testid={`invoice-item-${i}-qty`} /></div>
                      <div><label className="text-[10px] font-semibold text-[#5C6B69] uppercase">Unit</label>
                        <Input value={it.unit} className="bg-white border-[#E5E0D8] h-9"
                          onChange={(e) => setItem(i, { unit: e.target.value })} data-testid={`invoice-item-${i}-unit`} /></div>
                      <div><label className="text-[10px] font-semibold text-[#5C6B69] uppercase">Rate ₹</label>
                        <Input type="number" min="0" value={it.rate} className="bg-white border-[#E5E0D8] h-9"
                          onChange={(e) => setItem(i, { rate: Number(e.target.value) })} data-testid={`invoice-item-${i}-rate`} /></div>
                      <div><label className="text-[10px] font-semibold text-[#5C6B69] uppercase">Discount ₹</label>
                        <Input type="number" min="0" value={it.discount} className="bg-white border-[#E5E0D8] h-9"
                          onChange={(e) => setItem(i, { discount: Number(e.target.value) })} data-testid={`invoice-item-${i}-discount`} /></div>
                      <div><label className="text-[10px] font-semibold text-[#5C6B69] uppercase">GST %</label>
                        <Select value={String(it.gst_rate)} onValueChange={(v) => setItem(i, { gst_rate: Number(v) })}>
                          <SelectTrigger className="bg-white border-[#E5E0D8] h-9" data-testid={`invoice-item-${i}-gst`}><SelectValue /></SelectTrigger>
                          <SelectContent>{taxRates.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                        </Select></div>
                    </div>
                    <div className="text-right text-sm num text-[#0D4F4A] font-semibold">
                      Line total: {inr(r2((it.qty || 1) * (it.rate || 0) - (it.discount || 0)))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h3 className="font-display text-lg font-semibold">GST Configuration</h3>
              <Field label="Transaction Tax Type" required>
                <Select value={form.tax_type} onValueChange={(v) => setForm({ ...form, tax_type: v })}>
                  <SelectTrigger data-testid="invoice-tax-type" className="bg-white border-[#E5E0D8]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">Regular GST (taxable)</SelectItem>
                    <SelectItem value="exempt">GST Exempt</SelectItem>
                    <SelectItem value="nil_rated">Nil Rated (0%)</SelectItem>
                    <SelectItem value="non_gst">Non-GST / Out of scope</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Place of Supply" required hint="Determines CGST+SGST (intra-state) vs IGST (inter-state). Supplier state: Karnataka (29).">
                <StateSelect value={form.place_of_supply}
                  onChange={(v) => setForm({ ...form, place_of_supply: v, place_of_supply_code: "" })}
                  testid="invoice-place-of-supply" />
              </Field>
              <div className="flex items-center justify-between border border-[#E5E0D8] rounded-lg p-4">
                <div>
                  <div className="text-sm font-semibold">Reverse Charge</div>
                  <div className="text-xs text-[#5C6B69]">Tax payable by recipient (rare for travel services)</div>
                </div>
                <Switch checked={form.reverse_charge} onCheckedChange={(v) => setForm({ ...form, reverse_charge: v })}
                  data-testid="invoice-reverse-charge" />
              </div>
              <div className={cn("rounded-lg border p-4 text-sm font-medium",
                isIgst ? "bg-sky-50 border-sky-200 text-sky-800" : "bg-emerald-50 border-emerald-200 text-emerald-800")}
                data-testid="invoice-gst-type-indicator">
                {form.tax_type !== "regular" ? `${form.tax_type.replace("_", " ")} — no GST will be charged` :
                  isIgst ? `Inter-state supply → IGST applies (Place of supply: ${form.place_of_supply})` :
                  "Intra-state supply (Karnataka) → CGST + SGST applies"}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <h3 className="font-display text-lg font-semibold">Overall Discount</h3>
              <Field label="Invoice-level discount (₹)" hint="Distributed proportionally across items before GST. Item-level discounts can also be set in Step 3.">
                <Input type="number" min="0" value={form.overall_discount}
                  onChange={(e) => setForm({ ...form, overall_discount: Number(e.target.value) })}
                  data-testid="invoice-overall-discount" className="bg-white border-[#E5E0D8] w-56" />
              </Field>
              <Field label="Notes (printed on invoice)">
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="invoice-notes" className="bg-white border-[#E5E0D8]" />
              </Field>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <h3 className="font-display text-lg font-semibold">Payment / Advance <span className="text-xs font-normal text-[#5C6B69]">(optional — record now)</span></h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Amount received (₹)">
                  <Input type="number" min="0" value={form.payment.amount}
                    onChange={(e) => setForm({ ...form, payment: { ...form.payment, amount: Number(e.target.value) } })}
                    data-testid="invoice-payment-amount" className="bg-white border-[#E5E0D8]" />
                </Field>
                <Field label="Mode">
                  <Select value={form.payment.mode} onValueChange={(v) => setForm({ ...form, payment: { ...form.payment, mode: v } })}>
                    <SelectTrigger data-testid="invoice-payment-mode" className="bg-white border-[#E5E0D8]"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Cash", "UPI", "Bank Transfer", "Card", "Razorpay", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Reference / UTR">
                  <Input value={form.payment.reference} onChange={(e) => setForm({ ...form, payment: { ...form.payment, reference: e.target.value } })}
                    data-testid="invoice-payment-ref" className="bg-white border-[#E5E0D8]" />
                </Field>
              </div>
              <div className="flex gap-2 flex-wrap">
                {[0.25, 0.5, 1].map((pct) => (
                  <Button key={pct} size="sm" variant="outline"
                    onClick={() => setForm({ ...form, payment: { ...form.payment, amount: Math.round(totals.grand * pct) } })}
                    data-testid={`invoice-pay-${pct * 100}pct`}>
                    {pct === 1 ? "Full amount" : `${pct * 100}%`}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-4">
              <h3 className="font-display text-lg font-semibold">Review & Generate</h3>
              <div className="border border-[#E5E0D8] rounded-lg overflow-hidden text-sm">
                <table className="w-full">
                  <thead><tr className="bg-[#FAF8F5] text-xs uppercase text-[#5C6B69]">
                    <th className="text-left px-3 py-2">Item</th><th className="text-right px-3 py-2">Taxable</th>
                    <th className="text-right px-3 py-2">GST</th><th className="text-right px-3 py-2">Total</th></tr></thead>
                  <tbody>
                    {form.items.map((it, i) => {
                      const t = r2((it.qty || 1) * (it.rate || 0) - (it.discount || 0));
                      const rate = form.tax_type === "regular" ? Number(it.gst_rate || 0) : 0;
                      return (
                        <tr key={i} className="border-t border-[#E5E0D8]/60">
                          <td className="px-3 py-2">{it.description} <span className="text-xs text-[#5C6B69]">· SAC {it.sac || "—"} · {rate}%</span></td>
                          <td className="px-3 py-2 text-right num">{inr(t)}</td>
                          <td className="px-3 py-2 text-right num">{inr(r2(t * rate / 100))}</td>
                          <td className="px-3 py-2 text-right num font-semibold">{inr(r2(t + t * rate / 100))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-xs text-[#5C6B69] space-y-1">
                <div>Customer: <b>{customer?.name || "—"}</b> ({customer?.gstin ? "B2B" : "B2C"})</div>
                <div>Place of supply: <b>{form.place_of_supply || "—"}</b> · {isIgst ? "IGST" : "CGST + SGST"} · {form.reverse_charge ? "Reverse charge" : "Forward charge"}</div>
                {form.payment.amount > 0 && <div>Payment to record: <b>{inr(form.payment.amount)}</b> via {form.payment.mode}</div>}
              </div>
            </div>
          )}

          <div className="flex justify-between mt-8 pt-4 border-t border-[#E5E0D8]">
            <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-prev">
              <ArrowLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={() => canNext ? setStep(step + 1) : toast.error(step === 0 ? "Select a customer first" : "Complete this step")}
                className="bg-[#0D4F4A] hover:bg-[#136A63]" data-testid="wizard-next">
                Next <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" disabled={saving} onClick={() => submit("draft")} data-testid="invoice-save-draft">
                  <Save className="h-4 w-4 mr-1" /> Save Draft
                </Button>
                <Button disabled={saving} onClick={() => submit("issued")}
                  className="bg-[#C9A227] hover:bg-[#b8931f] text-[#092B28] font-semibold" data-testid="invoice-generate-button">
                  {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                  Generate {DOC_TYPE_LABELS[form.doc_type]}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-5 h-fit sticky top-20" data-testid="invoice-live-totals">
          <h3 className="font-display text-base font-semibold mb-4">Live Totals</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-[#5C6B69]">Taxable Value</dt><dd className="num">{inr(totals.taxable)}</dd></div>
            {!isIgst ? (
              <>
                <div className="flex justify-between"><dt className="text-[#5C6B69]">CGST</dt><dd className="num">{inr(totals.cgst)}</dd></div>
                <div className="flex justify-between"><dt className="text-[#5C6B69]">SGST</dt><dd className="num">{inr(totals.sgst)}</dd></div>
              </>
            ) : (
              <div className="flex justify-between"><dt className="text-[#5C6B69]">IGST</dt><dd className="num">{inr(totals.igst)}</dd></div>
            )}
            <div className="flex justify-between"><dt className="text-[#5C6B69]">Total GST</dt><dd className="num">{inr(totals.gst)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#5C6B69]">Round Off</dt><dd className="num">{totals.roundOff >= 0 ? "+" : ""}{totals.roundOff.toFixed(2)}</dd></div>
            <div className="flex justify-between pt-3 border-t-2 border-[#0D4F4A]">
              <dt className="font-display font-bold text-lg">Grand Total</dt>
              <dd className="num font-bold text-xl text-[#0D4F4A]" data-testid="invoice-grand-total">{inr(totals.grand)}</dd>
            </div>
            {form.payment.amount > 0 && (
              <>
                <div className="flex justify-between text-emerald-700"><dt>Paid now</dt><dd className="num">{inr(form.payment.amount)}</dd></div>
                <div className="flex justify-between text-red-600"><dt>Balance due</dt><dd className="num">{inr(Math.max(0, totals.grand - form.payment.amount))}</dd></div>
              </>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
}
